import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { filterConfig, mercadosTreeConfig } from "../utils/filterConfig";
import { formatKey } from "../utils/formatters";
import mercados from "../services/mercadosService";
import { FILTER_LAYER_SUPPORT } from "../utils/filterUtils";
import { COLORS_MERCADOS } from "../utils/styleUtils";

import {
  getDepartamentos,
  getProvinciasPorDepartamento,
  getDistritosPorProvincia,
} from "../services/ubigeosService";

import FilterAltIcon from "@mui/icons-material/FilterAltOutlined";
import SearchIcon from "@mui/icons-material/SearchOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

import "../styles/mici-ui.css";

// ============================================================
// PxSelect — dropdown custom estilo preview (.px-select look)
// - Single-select
// - Popup position: fixed para escapar el overflow:hidden del clip
// - Click-outside cierra; ESC cierra
// ============================================================
const PxSelect = ({ label, value, options, onChange, placeholder, multiple = false }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const triggerRef = useRef(null);
  const popupRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const updatePos = () => {
      if (!triggerRef.current) return;
      const r = triggerRef.current.getBoundingClientRect();
      const GAP = 4;
      const EDGE = 8; // respiro contra el borde del viewport
      const MIN_HEIGHT = 120;
      const ABS_MAX = 320;
      const vh = window.innerHeight;
      const availableBelow = vh - r.bottom - GAP - EDGE;
      const availableAbove = r.top - GAP - EDGE;

      // Si abajo no entra el mínimo y arriba hay más espacio, abrimos hacia arriba
      const openUp =
        availableBelow < MIN_HEIGHT && availableAbove > availableBelow;

      const space = openUp ? availableAbove : availableBelow;
      const maxHeight = Math.max(MIN_HEIGHT, Math.min(ABS_MAX, space));

      setPos({
        top: openUp ? r.top - GAP - maxHeight : r.bottom + GAP,
        left: r.left,
        width: r.width,
        maxHeight,
      });
    };
    const handleOutside = (e) => {
      if (triggerRef.current && triggerRef.current.contains(e.target)) return;
      if (popupRef.current && popupRef.current.contains(e.target)) return;
      setOpen(false);
    };
    const handleEsc = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    updatePos();
    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("keydown", handleEsc);
    window.addEventListener("resize", updatePos);
    window.addEventListener("scroll", updatePos, true);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("keydown", handleEsc);
      window.removeEventListener("resize", updatePos);
      window.removeEventListener("scroll", updatePos, true);
    };
  }, [open]);

  const isOptSelected = (opt) => {
    if (multiple) {
      const arr = Array.isArray(value) ? value : [];
      return arr.some((v) => String(v) === String(opt.value));
    }
    return String(opt.value) === String(value);
  };

  const handleSelect = (opt) => {
    const isReset = opt.value === "" || opt.value == null;
    if (multiple) {
      if (isReset) {
        onChange([]);
        setOpen(false);
        return;
      }
      const arr = Array.isArray(value) ? value : [];
      const exists = arr.some((v) => String(v) === String(opt.value));
      const next = exists
        ? arr.filter((v) => String(v) !== String(opt.value))
        : [...arr, opt.value];
      onChange(next);
      // popup queda abierto para permitir múltiples selecciones
    } else {
      onChange(opt.value);
      setOpen(false);
    }
  };

  let displayValue;
  if (multiple) {
    const arr = Array.isArray(value) ? value : [];
    if (arr.length === 0) {
      displayValue = placeholder || "Todos";
    } else {
      // Labels seleccionados separados por coma — el CSS del .px-select strong
      // trunca con ellipsis si exceden el ancho del trigger.
      const labels = arr
        .map(
          (v) =>
            (options || []).find((o) => String(o.value) === String(v))?.label
        )
        .filter(Boolean);
      displayValue = labels.join(", ");
    }
  } else {
    const selected = (options || []).find(
      (o) => String(o.value) === String(value)
    );
    displayValue = selected ? selected.label : placeholder || "Seleccionar";
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={`px-select ${open ? "is-open" : ""}`}
        onClick={() => setOpen((o) => !o)}
      >
        <span>{label}</span>
        <strong>{displayValue}</strong>
        <KeyboardArrowDownIcon
          style={{ fontSize: 16, color: "var(--text-muted)" }}
        />
      </button>
      {open && pos &&
        // Portal a document.body — escapa el overflow:hidden + backdrop-filter
        // del .px-panel-clip que estaba recortando el popup.
        createPortal(
          <div
            ref={popupRef}
            className="px-select-popup"
            style={{
              top: pos.top,
              left: pos.left,
              width: pos.width,
              maxHeight: pos.maxHeight,
            }}
          >
            <div className="px-select-popup-scroll">
              {(options || []).map((opt, idx) => {
                const isReset = opt.value === "" || opt.value == null;
                return (
                  <button
                    key={opt.value ?? `opt-${idx}`}
                    type="button"
                    className={`px-select-option ${
                      isReset ? "is-reset" : ""
                    } ${isOptSelected(opt) ? "is-selected" : ""}`}
                    onClick={() => handleSelect(opt)}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>,
          document.body
        )}
    </>
  );
};


// Filtros siempre ocultos — no tienen backing en ningún sublayer del
// MapServer correspondiente. (Ubigeo en parques/zonas: la capa no tiene
// `id_ubigeo`. tipo_convenio: tampoco existe en ningún sublayer de parques.)
// Mercados sí se queda con ubigeo porque su filtrado es local.
const HIDDEN_FILTERS_BY_TAB = {
  parquesIndustriales: [
    "departamento",
    "provincia",
    "distrito",
    "tipo_convenio",
  ],
  zonasIndustriales: ["departamento", "provincia", "distrito"],
};

// Decide si un filtro debe mostrarse en el panel, dado el set actual de
// sublayers visibles. Un filtro aparece si al menos uno de los sublayers
// visibles soporta su SQL field (según FILTER_LAYER_SUPPORT).
// FILTER_LAYER_SUPPORT está keyed por SQL field (clave), pero el panel
// iterar filterConfig que está keyed por nombre visible (disponibilidad/uso/
// etc.) — así que resolvemos la clave desde el config.
const isFilterApplicable = (tabKey, configKey, configEntry, visibleLayerIds) => {
  const support = FILTER_LAYER_SUPPORT[tabKey];
  if (!support) return true; // mercados — no aplica la lógica per-layer
  // El clave SQL está en la primera opción del array del filtro.
  const clave =
    Array.isArray(configEntry) && configEntry[0]?.clave
      ? configEntry[0].clave
      : configKey;
  const supported = support[clave];
  if (!supported) return true; // filtro sin entrada → no restringido (ubigeo)
  if (!Array.isArray(visibleLayerIds)) return false;
  return supported.some((id) => visibleLayerIds.includes(id));
};

const AREA_RANGES = [
  { value: "all", label: "Todos los tamaños" },
  { value: "lt1000", label: "Menos de 1,000 m²" },
  { value: "1000_2499", label: "De 1,000 a 2,499 m²" },
  { value: "2500_4999", label: "De 2,500 a 4,999 m²" },
  { value: "5000_10000", label: "De 5,000 a 10,000 m²" },
  { value: "gt10000", label: "Más de 10,000 m²" },
];

// getAreaWhere se movió a filterUtils.js — ahora se usa desde los wrappers
// per-layer en MapWithOutSidebar, no aquí.

const TAB_COLORS = {
  0: "#14B8A6", // Mercados Minoristas - teal
  2: "#F59E0B", // Parques Industriales - amber
  3: "#8B5CF6", // Zonas Industriales - violet
};

// Convierte "EN EJECUCIÓN" → "En Ejecución" (mayúscula sólo en la inicial de cada palabra)
const toTitleCase = (s) =>
  String(s || "")
    .toLowerCase()
    .replace(/(^|\s)\S/g, (t) => t.toUpperCase());

const FilterPanel = ({
  isOpen,
  activeTab,
  onToggle,
  onFilterChange,
  onArcgisFilterChange,
  onCentroideChange,
  onLayerChange,
  layerId,
  count,
  selectedPairs,
  toggleEstado,
}) => {
  const [filters, setFilters] = useState({});
  const [checkboxes, setCheckboxes] = useState([false, false, false]);
  const [checkboxesParques, setCheckboxesParques] = useState([
    false,
    false,
    false,
    false,
    false,
  ]);
  const [searchTerm, setSearchTerm] = useState("");
  const [areaFilter, setAreaFilter] = useState("all");
  const [estadoCounts, setEstadoCounts] = useState({});
  const typingRef = useRef(null);

  const tabMapping = {
    0: "mercadosMinoristas",
    1: "mercadosMayoristas",
    2: "parquesIndustriales",
    3: "zonasIndustriales",
  };

  const currentConfigKey = tabMapping[activeTab];
  const currentConfig = useMemo(() => filterConfig[currentConfigKey] || {}, [currentConfigKey]);

  useEffect(() => {
    // Reset completo del estado de filtros al cambiar de tab — no tiene
    // sentido compartir filtros entre módulos (filtrar "ate" en Parques
    // no debería persistir al volver a Mercados, etc).
    setFilters({});
    setSearchTerm("");
    setAreaFilter("all");

    if (activeTab === 3) {
      setCheckboxes([true, false, false]);
    } else if (activeTab === 2) {
      setCheckboxesParques([true, true, true, false, false]);
    } else {
      setCheckboxes([true, true, true]);
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== 0) return;
    mercados.getMercadosMinoristas().then((data) => {
      const arr = Array.isArray(data) ? data : (data?.default || []);
      const counts = {};
      let totalValid = 0;
      arr.forEach((item) => {
        const id = item.id_estado_intervencion;
        if (id != null) {
          const modId = item.id_modalidad;
          const isValid = COLORS_MERCADOS[Number(id)]?.modalidades?.[Number(modId)] !== undefined;
          if (isValid) {
            counts[id] = (counts[id] || 0) + 1;
            totalValid += 1;
          }
        }
      });
      setEstadoCounts({ ...counts, total: totalValid });
    }).catch(() => {});
  }, [activeTab]);

  useEffect(() => {
    if (!onLayerChange) return;

    if (activeTab === 3) {
      const sel = [];
      if (checkboxes?.[0]) sel.push(2);
      if (checkboxes?.[1]) sel.push(1);
      if (checkboxes?.[2]) sel.push(0);
      onLayerChange(sel);
      return;
    }

    if (activeTab === 2) {
      const sel = [];
      if (checkboxesParques?.[0]) sel.push(0);
      if (checkboxesParques?.[1]) sel.push(1);
      if (checkboxesParques?.[2]) sel.push(2);
      if (checkboxesParques?.[3]) sel.push(3);
      if (checkboxesParques?.[4]) sel.push(4);
      onLayerChange(sel);
      return;
    }

    onLayerChange([]);
  }, [activeTab, checkboxes, checkboxesParques, onLayerChange]);

  useEffect(() => {
    let initialFilters = {};

    switch (activeTab) {
      case 0:
        if (
          currentConfig.tipo &&
          currentConfig.tipo.length > 0 &&
          currentConfig.tipo[0].clave
        ) {
          initialFilters[currentConfig.tipo[0].clave] = [1];
        }
        break;
      case 1:
        if (
          currentConfig.tipo &&
          currentConfig.tipo.length > 0 &&
          currentConfig.tipo[0].clave
        ) {
          initialFilters[currentConfig.tipo[0].clave] = [3];
        }
        break;
      case 2:
        if (
          currentConfig.tipo &&
          currentConfig.tipo.length > 0 &&
          currentConfig.tipo[0].clave
        ) {
          initialFilters[currentConfig.tipo[0].clave] = [1];
        }
        break;
      case 3:
        if (
          currentConfig.tipo &&
          currentConfig.tipo.length > 0 &&
          currentConfig.tipo[0].clave
        ) {
          initialFilters[currentConfig.tipo[0].clave] = [2];
        }
        break;
      default:
        break;
    }

    setFilters(initialFilters);
  }, [activeTab, currentConfig]);

  const handleChange = (field, value) => {
    const fieldConfig = currentConfig[field];
    let fieldName = field;

    const isUbigeoField = ["departamento", "provincia", "distrito"].includes(field);

    const isClearing =
      value === null ||
      value === undefined ||
      value === "" ||
      (Array.isArray(value) && value.length === 0);

    const safeValue = isClearing
      ? []
      : Array.isArray(value)
        ? value
        : [value];

    if (!isUbigeoField && fieldConfig && fieldConfig.length > 0 && fieldConfig[0].clave) {
      fieldName = fieldConfig[0].clave;
    }

    const newFilters = { ...filters };

    if (isUbigeoField) {
      newFilters[field] = safeValue;
      if (field === "departamento") {
        newFilters["provincia"] = [];
        newFilters["distrito"] = [];
      }
      if (field === "provincia") {
        newFilters["distrito"] = [];
      }
    } else {
      newFilters[fieldName] = safeValue;
    }

    setFilters(newFilters);

    if (
      (field === "departamento" ||
        field === "provincia" ||
        field === "distrito") &&
      (activeTab === 3 || activeTab === 2 || activeTab === 0) &&
      safeValue.length > 0
    ) {
      const selectedOption = fieldConfig.find(
        (option) => option.value === safeValue[0]
      );
      if (selectedOption && selectedOption.centroide) {
        const zoomMap = { departamento: 8, provincia: 10, distrito: 12 };
        onCentroideChange({
          centroide: selectedOption.centroide,
          zoom: selectedOption.zoom || zoomMap[field] || 8,
        });
      }
    }
  };

  const handleApplyFilters = useCallback(async (manualSearchTerm = null) => {
    try {
      const termToUse = typeof manualSearchTerm === "string" ? manualSearchTerm : searchTerm;

      const arcgisFilters = { ...filters };

      // Resolvemos los labels seleccionados (nombres) para los filtros de
      // ubigeo. Los items vienen del API con nombres (item.departamento,
      // item.provincia, item.distrito), no con códigos, así que comparamos por
      // label. (Antes comparábamos `item.departamento` contra el código "13",
      // siempre devolvía 0 resultados; luego intenté comparar por prefijo de
      // id_ubigeo pero el API no devuelve códigos confiables para todos los
      // items — el match por nombre es más estable.)
      const norm = (s) => String(s || "").trim().toUpperCase();
      const depCode = filters.departamento?.[0];
      const provCode = filters.provincia?.[0];
      const distCode = filters.distrito?.[0];
      const depLabel = depCode
        ? getDepartamentos().find((o) => String(o.value) === String(depCode))?.label
        : null;
      const provLabel = provCode
        ? getProvinciasPorDepartamento(depCode).find(
            (o) => String(o.value) === String(provCode)
          )?.label
        : null;
      const distLabel = distCode
        ? getDistritosPorProvincia(provCode).find(
            (o) => String(o.value) === String(distCode)
          )?.label
        : null;

      const localFilters = Object.fromEntries(
        Object.entries(filters).filter(
          ([key]) =>
            ![
              "departamento",
              "provincia",
              "distrito",
            ].includes(key)
        )
      );

      if (activeTab === 2 || activeTab === 3) {
        // Emitimos los filtros RAW (sin __rawWhere precomputado). Cada
        // ParqueSublayer/ZonaSublayer construye su propio WHERE en base a su
        // schema vía buildLayerWhere. Para parques agregamos __area para que
        // el wrapper de /4 (lotes) pueda incluir el rango de área en su WHERE.
        const payload = { ...arcgisFilters };
        if (activeTab === 2 && areaFilter && areaFilter !== "all") {
          payload.__area = areaFilter;
        }
        onArcgisFilterChange(payload);
        onFilterChange([]);
        return;
      }

      let dataModule;

      switch (activeTab) {
        case 0:
        default:
          dataModule = await mercados.getMercadosMinoristas();
          break;
      }

      const data = dataModule.default || dataModule;

      if (activeTab === 0) {
        // Si no hay pares seleccionados (todo desactivado en Legend + chips), no muestres nada
        if (!selectedPairs || selectedPairs.size === 0) {
          onFilterChange([]);
          return;
        }

        const filteredDataTree = data.filter((item) => {
          // Filtros de ubigeo por nombre (ver nota arriba)
          if (depLabel && norm(item.departamento) !== norm(depLabel)) return false;
          if (provLabel && norm(item.provincia) !== norm(provLabel)) return false;
          if (distLabel && norm(item.distrito) !== norm(distLabel)) return false;

          // Búsqueda por texto (mín 3 chars)
          const term = (termToUse || "").trim().toLowerCase();
          if (term.length >= 3) {
            const hasMatch =
              (item.nombre && String(item.nombre).toLowerCase().includes(term)) ||
              (item.cui && String(item.cui).toLowerCase().includes(term));
            if (!hasMatch) return false;
          }

          // Filtro principal: pair (estado, modalidad) en selectedPairs
          const pairKey = `${item.id_estado_intervencion}-${item.id_modalidad}`;
          return selectedPairs.has(pairKey);
        });

        onFilterChange(filteredDataTree);

      } else {
        onArcgisFilterChange(arcgisFilters);

        const filteredData = data.filter((item) => {
          if (depLabel && norm(item.departamento) !== norm(depLabel)) return false;
          if (provLabel && norm(item.provincia) !== norm(provLabel)) return false;
          if (distLabel && norm(item.distrito) !== norm(distLabel)) return false;

          const term = (termToUse || "").trim().toLowerCase();
          if (term.length >= 3) {
            const hasMatch =
              (item.nombre && String(item.nombre).toLowerCase().includes(term)) ||
              (item.cui && String(item.cui).toLowerCase().includes(term));

            if (!hasMatch) return false;
          }

          return Object.entries(localFilters).every(([key, value]) => {
            if (Array.isArray(value) && value.length > 0) {
              const fieldConfig = currentConfig[key];
              let fieldName = key;

              if (fieldConfig && fieldConfig.length > 0 && fieldConfig[0].clave) {
                fieldName = fieldConfig[0].clave;
              }

              if (typeof item[fieldName] === "number") {
                return value.includes(item[fieldName]);
              }
              const numericValue = Number(item[fieldName]);
              if (!isNaN(numericValue)) {
                return value.includes(numericValue);
              }
              if (typeof item[fieldName] === "string") {
                return value.includes(item[fieldName]);
              }
              return false;
            }
            return true;
          });
        });

        onFilterChange(filteredData);
      }

    } catch (error) {
      console.error("Error al aplicar filtros:", error);
    }
  }, [searchTerm, filters, activeTab, areaFilter, onArcgisFilterChange, onFilterChange, currentConfig, selectedPairs]);


  const firstRender = useRef(true);
  useEffect(() => {
    if (!firstRender.current) {
      handleApplyFilters();
    }
    firstRender.current = false;
  }, [selectedPairs, handleApplyFilters, activeTab, areaFilter, filters]);

  // ============================================================
  // Render
  // ============================================================
  return (
    <aside className={`px-panel ${!isOpen ? "is-collapsed" : ""}`}>
      <div className="px-panel-clip">
        <div className="px-panel-inner">
          <div className="px-panel-header">
            <div className="px-panel-title">
              <FilterAltIcon style={{ fontSize: 18 }} />
              <h2>Filtros</h2>
              {/* Siempre rendea para reservar el ancho — antes aparecía
                  cuando el count cargaba (null → N) causando un layout
                  shift visible. Placeholder "…" mientras espera. */}
              <span
                className="px-pill"
                style={{ "--c": TAB_COLORS[activeTab] || "var(--produce-red)" }}
              >
                {count != null ? `${count} resultados` : "…"}
              </span>
            </div>
          </div>

          {/* Búsqueda — siempre primero en todos los tabs */}
          <div className="px-section">
            <h3>Búsqueda</h3>
            <div className="px-search">
              <SearchIcon style={{ fontSize: 16 }} />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => {
                  const value = e.target.value;
                  setSearchTerm(value);
                  if (typingRef.current) clearTimeout(typingRef.current);

                  if (!value.trim()) {
                    // Búsqueda vacía: re-aplicar el resto de filtros (selectedPairs, ubigeo)
                    // sin filtro por texto. NO usar onFilterChange(null) porque eso vuelve a
                    // mostrar TODO ignorando los chips de estado / clicks en la leyenda.
                    if (activeTab === 0 || activeTab === 1) {
                      handleApplyFilters("");
                    }
                    if (activeTab === 2 || activeTab === 3) {
                      // Resetea solo el __text — los filtros del panel
                      // (disponibilidad/uso/etc.) quedan tal cual.
                      onArcgisFilterChange?.({ __text: "" });
                    }
                    return;
                  }

                  typingRef.current = setTimeout(async () => {
                    if (activeTab === 0 || activeTab === 1) {
                      await handleApplyFilters(value);
                      return;
                    }
                    if (activeTab === 2 || activeTab === 3) {
                      if (value.trim().length >= 3) {
                        const safe = value.trim().replace(/'/g, "''");
                        onArcgisFilterChange?.({ __text: safe });
                      } else {
                        onArcgisFilterChange?.({ __text: "" });
                      }
                    }
                  }, 300);
                }}
                placeholder="Ingrese término de búsqueda..."
              />
            </div>
          </div>

          {activeTab === 3 && (
            <div className="px-section">
              <h3>Capas a mostrar</h3>
              <label className="px-check">
                <input
                  type="checkbox"
                  checked={checkboxes[0]}
                  onChange={(e) =>
                    setCheckboxes((prev) => [e.target.checked, prev[1], prev[2]])
                  }
                />
                <span>Capa Zonas Industriales</span>
              </label>
              <label className="px-check">
                <input
                  type="checkbox"
                  checked={checkboxes[1]}
                  onChange={(e) =>
                    setCheckboxes((prev) => [prev[0], e.target.checked, prev[2]])
                  }
                />
                <span>Capa Tipo Zonificación</span>
              </label>
              <label className="px-check">
                <input
                  type="checkbox"
                  checked={checkboxes[2]}
                  onChange={(e) =>
                    setCheckboxes((prev) => [prev[0], prev[1], e.target.checked])
                  }
                />
                <span>Capa Unidades Inmobiliarias</span>
              </label>
            </div>
          )}

          {activeTab === 2 && (
            <div className="px-section">
              <h3>Capas a mostrar</h3>
              <label className="px-check">
                <input
                  type="checkbox"
                  checked={checkboxesParques[1]}
                  onChange={(e) =>
                    setCheckboxesParques((prev) => [
                      prev[0],
                      e.target.checked,
                      prev[2],
                      prev[3],
                      prev[4],
                    ])
                  }
                />
                <span>Zonas Económicas Especiales</span>
              </label>
              <label className="px-check">
                <input
                  type="checkbox"
                  checked={checkboxesParques[2]}
                  onChange={(e) =>
                    setCheckboxesParques((prev) => [
                      prev[0],
                      prev[1],
                      e.target.checked,
                      prev[3],
                      prev[4],
                    ])
                  }
                />
                <span>Parques Industriales</span>
              </label>
              <label className="px-check px-check--indent">
                <input
                  type="checkbox"
                  checked={checkboxesParques[3]}
                  onChange={(e) =>
                    setCheckboxesParques((prev) => [
                      prev[0],
                      prev[1],
                      prev[2],
                      e.target.checked,
                      prev[4],
                    ])
                  }
                />
                <span>Zonificación Industriales</span>
              </label>
              <label className="px-check px-check--indent">
                <input
                  type="checkbox"
                  checked={checkboxesParques[4]}
                  onChange={(e) =>
                    setCheckboxesParques((prev) => [
                      prev[0],
                      prev[1],
                      prev[2],
                      prev[3],
                      e.target.checked,
                    ])
                  }
                />
                <span>Unidades Inmobiliarias</span>
              </label>
            </div>
          )}

          {/* Estado de intervención — chips (tab 0 — Mercados) */}
          {activeTab === 0 && (
            <div className="px-section">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                <h3 style={{ margin: 0 }}>Estado de intervención</h3>
                {estadoCounts.total != null && (
                  <span className="px-pill" style={{ "--c": "var(--produce-red)", fontSize: "11px", fontWeight: 500 }}>
                    Total: {estadoCounts.total}
                  </span>
                )}
              </div>
              <div className="px-chips">
                {mercadosTreeConfig.map((estado) => {
                  const estadoPairs = estado.children.map(
                    (m) => `${estado.id}-${m.id}`
                  );
                  const allSelected =
                    selectedPairs && estadoPairs.every((p) => selectedPairs.has(p));
                  const someSelected =
                    selectedPairs && estadoPairs.some((p) => selectedPairs.has(p));
                  const cls = allSelected
                    ? "is-selected"
                    : someSelected
                      ? "is-partial"
                      : "";
                  const label = toTitleCase(estado.label);
                  const estadoCount = estadoCounts[estado.id];
                  // Color representativo por estado (tono medio de cada familia)
                  const ESTADO_COLORS = {
                    1: "#EA580C", // EJECUTADAS — orange-600
                    2: "#10B981", // EN EJECUCIÓN — emerald-500
                    4: "#3B82F6", // EXPEDIENTE TÉCNICO — blue-500
                    6: "#F59E0B", // PREINVERSIÓN — amber-500
                    7: "#64748B", // CONSULTAS TÉCNICAS — slate-500
                  };
                  const chipColor = ESTADO_COLORS[estado.id] || "var(--produce-red)";
                  return (
                    <button
                      key={estado.id}
                      type="button"
                      className={`px-chip ${cls}`}
                      style={{ "--chip-color": chipColor }}
                      onClick={() => toggleEstado && toggleEstado(estado.id)}
                      title={
                        allSelected ? `Ocultar ${label}` : `Mostrar ${label}`
                      }
                    >
                      {label}
                      {estadoCount != null && (
                        <span className="px-chip-count">{estadoCount}</span>
                      )}
                    </button>
                  );
                })}
              </div>
              <p className="px-section-hint">
                Cada modalidad se filtra desde la <strong>leyenda</strong> a la derecha.
              </p>
            </div>
          )}

          {(() => {
            // Filtros visibles = los que no estén en HIDDEN_FILTERS_BY_TAB
            // y, además, sean aplicables a al menos uno de los sublayers
            // visibles (según FILTER_LAYER_SUPPORT). Esto hace que filtros
            // como "disponibilidad"/"uso" en parques solo aparezcan cuando
            // Unidades Inmobiliarias está prendida; "zona"/"uso"/"ocupacion"
            // en zonas solo cuando Lotes está prendido; etc.
            const hidden = HIDDEN_FILTERS_BY_TAB[currentConfigKey] || [];
            const visibleEntries = Object.entries(currentConfig).filter(
              ([key, configEntry]) => {
                if (hidden.includes(key)) return false;
                const isUbigeoField = ["departamento", "provincia", "distrito"].includes(key);
                if (activeTab === 0 && !isUbigeoField) return false;
                if (
                  !isFilterApplicable(
                    currentConfigKey,
                    key,
                    configEntry,
                    layerId
                  )
                ) {
                  return false;
                }
                return true;
              }
            );
            const hasUbigeo = visibleEntries.some(([key]) =>
              ["departamento", "provincia", "distrito"].includes(key)
            );
            // Área aplica solo en parques /4 (lotes).
            const showAreaFilter =
              activeTab === 2 && Array.isArray(layerId) && layerId.includes(4);
            if (visibleEntries.length === 0 && !showAreaFilter) return null;
            const sectionTitle = hasUbigeo ? "Ubicación" : "Filtros";
            return (
          <div className="px-section">
            <h3>{sectionTitle}</h3>

            {visibleEntries.map(([key, options]) => {
              const fieldConfig = currentConfig[key];
              let fieldName = key;
              const isUbigeoField = ["departamento", "provincia", "distrito"].includes(key);

              if (!isUbigeoField && fieldConfig && fieldConfig.length > 0 && fieldConfig[0].clave) {
                fieldName = fieldConfig[0].clave;
              }

              let optionsToUse = options;

              if (isUbigeoField) {
                if (key === "departamento") {
                  optionsToUse = getDepartamentos();
                } else if (key === "provincia") {
                  const selectedDep = filters["departamento"]?.[0] || null;
                  optionsToUse = getProvinciasPorDepartamento(selectedDep);
                } else if (key === "distrito") {
                  const selectedProv = filters["provincia"]?.[0] || null;
                  optionsToUse = getDistritosPorProvincia(selectedProv);
                }
              }

              // Normalizamos opciones: title case en label + opción "Todos"
              // como reset al inicio para mantener el mismo look en todos los
              // dropdowns (ubigeo y no-ubigeo).
              const finalOptions = [
                { label: "Todos", value: "" },
                ...(optionsToUse || []).map((o) => ({
                  ...o,
                  label: toTitleCase(o.label),
                })),
              ];

              let valueForSelect;
              if (isUbigeoField) {
                valueForSelect = (filters[key] && filters[key][0]) || null;
              } else {
                valueForSelect = filters[fieldName] || [];
              }

              // Todos los dropdowns usan PxSelect. Ubigeo es single-select,
              // los demás multi-select. formatKey limpia el "_" del key
              // ("tipo_convenio" → "Tipo Convenio").
              const labelText = formatKey(key);
              return (
                <div key={key} className="px-filter-field">
                  <PxSelect
                    label={labelText}
                    value={valueForSelect}
                    onChange={(v) => handleChange(key, v)}
                    options={finalOptions}
                    placeholder="Todos"
                    multiple={!isUbigeoField}
                  />
                </div>
              );
            })}
            {showAreaFilter && (
              <div className="px-filter-field">
                <PxSelect
                  label="Área de lote (m²)"
                  value={areaFilter}
                  onChange={(v) => setAreaFilter(v || "all")}
                  options={AREA_RANGES}
                  placeholder="Todos los tamaños"
                />
              </div>
            )}
          </div>
            );
          })()}

        </div>
      </div>

      <button
        type="button"
        className="px-panel-handle"
        onClick={onToggle}
        title={isOpen ? "Cerrar filtros" : "Abrir filtros"}
      >
        <ChevronLeftIcon style={{ fontSize: 18 }} />
      </button>
    </aside>
  );
};

export default FilterPanel;
